import { ShieldCheck, Award, BadgeCheck, MessageCircle } from "lucide-react";
import { SALES } from "../data/constants";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";

/**
 * Sales identity card - E-E-A-T trust signal.
 * Shows Kiel's professional headshot, name, title, credentials.
 */
export default function SalesIdentity({ compact = false, customMessage }) {
  const { settings } = useSettings();
  const photo = settings.sales_photo || SALES.photo;
  const name = settings.sales_name || SALES.name;
  const title = settings.sales_title || SALES.title;
  const phone = settings.sales_phone || SALES.phone;
  const phoneIntl = settings.sales_phone_intl || SALES.phoneIntl;
  if (compact) {
    return (
      <div
        className="flex items-center gap-4 p-5 rounded-2xl border border-[#E5E5EA] bg-white"
        data-testid="sales-identity-compact"
      >
        <img
          src={photo}
          alt={`${name} - ${title}`}
          loading="lazy"
          decoding="async"
          width="120"
          height="120"
          className="w-14 h-14 rounded-full object-cover border border-[#E5E5EA]"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#0F0F0F] truncate">
            {name}
          </p>
          <p className="text-xs text-[#6E6E73] truncate">{title}</p>
        </div>
        <a
          href={buildWaLink(settings, customMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWhatsAppLead("sales-compact")}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-white px-4 py-2 text-xs font-medium hover:bg-[#128C7E] transition-colors"
          data-testid="sales-identity-compact-wa"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Chat
        </a>
      </div>
    );
  }

  return (
    <section
      className="bg-[#F5F5F7] relative overflow-hidden"
      data-testid="sales-identity-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-white border border-[#E5E5EA] relative">
              <img
                src={photo}
                alt={`${name} - ${title} BYD Haka Auto Cibubur`}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width="800"
                height="1000"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-3 border border-white/50">
                <BadgeCheck className="w-5 h-5 text-[#25D366] shrink-0" />
                <span className="text-xs font-medium text-[#0F0F0F]">
                  Certified BYD Product Specialist
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
              Konsultan Resmi Anda
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1]">
              Bicara Langsung Dengan{" "}
              <span className="italic font-light">{name}</span>,
              <br className="hidden sm:block" /> {title}.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-[#6E6E73] leading-relaxed max-w-xl">
              {SALES.experience}. Setiap konsultasi dibantu secara personal
              dengan transparansi harga, hitungan kredit multi-leasing, dan
              pendampingan hingga serah terima unit di {`rumah/kantor`} Anda.
            </p>

            <ul className="mt-8 space-y-3">
              {SALES.credentials.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-sm text-[#0F0F0F]"
                >
                  <ShieldCheck className="w-4 h-4 mt-0.5 text-[#0F0F0F] shrink-0" />
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={buildWaLink(settings, customMessage)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppLead("sales-identity")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-7 py-4 text-sm font-medium transition-colors"
                data-testid="sales-identity-primary-cta"
              >
                <MessageCircle className="w-4 h-4" />
                Chat WhatsApp {name}
              </a>
              <a
                href={`tel:+${phoneIntl}`}
                className="inline-flex items-center justify-center rounded-full bg-white hover:bg-[#F5F5F7] text-[#0F0F0F] px-7 py-4 text-sm font-medium border border-[#E5E5EA] transition-colors"
                data-testid="sales-identity-call"
              >
                Telepon {phone}
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 pt-6 border-t border-[#E5E5EA]">
              <div>
                <p className="text-2xl font-medium text-[#0F0F0F]">500+</p>
                <p className="text-xs text-[#86868B] mt-1">Unit Terjual</p>
              </div>
              <div className="h-10 w-px bg-[#E5E5EA]" />
              <div>
                <p className="text-2xl font-medium text-[#0F0F0F]">4.9/5</p>
                <p className="text-xs text-[#86868B] mt-1">Rating Pelanggan</p>
              </div>
              <div className="h-10 w-px bg-[#E5E5EA]" />
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#0F0F0F]" />
                <p className="text-xs text-[#86868B]">Top Sales 2024</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
